import {
  createSharedResourceDescriptor,
  type SharedResourceDescriptor,
} from "./shared-resource-descriptor";
import {
  createSharedResourceShareDrawerAdapter,
  type SharedResourceShareDrawerAdapter,
  type SharedResourceShareDrawerRecord,
} from "./shared-resource-share-drawer-adapter";
import type {
  SharedResourceShareCapability,
  SharedResourceShareFormAdapter,
  SharedResourceShareMutationPayload,
} from "./shared-resource-share-form-adapter";

type SharedResourceReadableRecord<TAccessCapability extends string> = {
  owner_user_id?: number | string | null;
  share_summary: {
    current_user_capabilities: TAccessCapability[];
  };
};

export type SharedResourceBinderConfig<
  TRecord extends SharedResourceReadableRecord<TAccessCapability> &
    SharedResourceShareDrawerRecord<TShareCapability>,
  TAccessCapability extends string = SharedResourceShareCapability,
  TShareCapability extends SharedResourceShareCapability = Extract<
    TAccessCapability,
    SharedResourceShareCapability
  >,
  TPayload extends SharedResourceShareMutationPayload<
    TShareCapability,
    string | number
  > = SharedResourceShareMutationPayload<TShareCapability>,
  TResourceKey extends string | number = number,
> = SharedResourceDescriptor &
  Pick<
    SharedResourceShareDrawerAdapter<TRecord, TShareCapability, TPayload, TResourceKey>,
    | "resourceKey"
    | "i18nPrefix"
    | "persistenceKeyPrefix"
    | "updateResource"
    | "getShareModeLabel"
    | "supportedCapabilities"
    | "identifierLabelKey"
    | "titleLabelKey"
    | "getIdentifierText"
    | "getTitleText"
  > & {
    getResourceKey?: (record: TRecord) => TResourceKey;
    shareFormAdapter: SharedResourceShareFormAdapter<TRecord, TShareCapability, TPayload>;
  };

export function createSharedResourceBinder<
  TRecord extends SharedResourceReadableRecord<TAccessCapability> &
    SharedResourceShareDrawerRecord<TShareCapability>,
  TAccessCapability extends string = SharedResourceShareCapability,
  TShareCapability extends SharedResourceShareCapability = Extract<
    TAccessCapability,
    SharedResourceShareCapability
  >,
  TPayload extends SharedResourceShareMutationPayload<
    TShareCapability,
    string | number
  > = SharedResourceShareMutationPayload<TShareCapability>,
  TResourceKey extends string | number = number,
>(config: SharedResourceBinderConfig<
  TRecord,
  TAccessCapability,
  TShareCapability,
  TPayload,
  TResourceKey
>) {
  const descriptor = createSharedResourceDescriptor<TAccessCapability>({
    resourceKey: config.resourceKey,
    scopeI18nPrefix: config.scopeI18nPrefix,
  });
  const shareDrawerAdapter = createSharedResourceShareDrawerAdapter<
    TRecord,
    TShareCapability,
    TPayload,
    TResourceKey
  >({
    resourceKey: config.resourceKey,
    i18nPrefix: config.i18nPrefix,
    persistenceKeyPrefix: config.persistenceKeyPrefix,
    toFormValues: config.shareFormAdapter.toFormValues,
    normalizePayload: config.shareFormAdapter.normalizePayload,
    getResourceKey: config.getResourceKey ?? ((record) => record.id as TResourceKey),
    updateResource: config.updateResource,
    getShareModeLabel: config.getShareModeLabel,
    supportedCapabilities: config.supportedCapabilities,
    ...(config.identifierLabelKey === undefined ? {} : { identifierLabelKey: config.identifierLabelKey }),
    ...(config.titleLabelKey === undefined ? {} : { titleLabelKey: config.titleLabelKey }),
    ...(config.getIdentifierText === undefined ? {} : { getIdentifierText: config.getIdentifierText }),
    ...(config.getTitleText === undefined ? {} : { getTitleText: config.getTitleText }),
  });

  return {
    ...descriptor,
    shareDrawerAdapter,
  };
}
